/**
 * LeafletMap - Base Component
 * Component nền tảng cho tất cả các map Leaflet trong ứng dụng
 *
 * Features:
 * - Multiple tile layers (CartoDB, OSM, Satellite)
 * - Responsive container
 * - Loading state
 * - forwardRef để parent có thể access map instance
 */
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix Leaflet default marker icon issue with webpack/vite
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

// Fix default icon
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

// CARTO basemaps bắt buộc API key (https://carto.com/basemaps/apikey).
// Thiếu key, tile trả về ảnh "API KEY REQUIRED" thay vì bản đồ.
const CARTO_API_KEY = import.meta.env.VITE_CARTO_API_KEY || ''
const cartoUrl = (style) =>
  `https://{s}.basemaps.cartocdn.com/${style}/{z}/{x}/{y}{r}.png` +
  (CARTO_API_KEY ? `?key=${CARTO_API_KEY}` : '')
const CARTO_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'

const OSM_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'

// Tile layer presets
export const TILE_LAYERS = {
  // CartoDB Positron - Light, clean style
  // Chưa cấu hình VITE_CARTO_API_KEY thì dùng OpenStreetMap (không cần key)
  cartodb_light: CARTO_API_KEY
    ? { url: cartoUrl('light_all'), attribution: CARTO_ATTRIBUTION, name: 'Light' }
    : { url: OSM_URL, attribution: OSM_ATTRIBUTION, name: 'Light' },
  // CartoDB Dark
  cartodb_dark: {
    url: cartoUrl('dark_all'),
    attribution: CARTO_ATTRIBUTION,
    name: 'Dark',
  },
  // OpenStreetMap Standard
  osm: {
    url: OSM_URL,
    attribution: OSM_ATTRIBUTION,
    name: 'OpenStreetMap',
  },
  // Esri Satellite (free)
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; <a href="https://www.esri.com/">Esri</a>',
    name: 'Satellite',
  },
  // Esri Street Map
  street: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; <a href="https://www.esri.com/">Esri</a>',
    name: 'Street',
  },
  // OpenTopoMap
  topo: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
    name: 'Topo',
  },
}

// Map Google style names to Leaflet tile layers
export const GOOGLE_STYLE_MAP = {
  roadmap: 'cartodb_light',
  satellite: 'satellite',
  terrain: 'topo',
  hybrid: 'satellite', // Leaflet satellite is close to hybrid
}

// Default center: Da Nang
const DEFAULT_CENTER = [16.0544, 108.2022]
const DEFAULT_ZOOM = 13

// LeafletMap ref interface
// - getMap(): L.Map instance
// - panTo(lat, lng, zoom?)
// - fitBounds(bounds, options?)
// - setView(lat, lng, zoom)
// - invalidateSize()

const LeafletMap = forwardRef(function LeafletMap(
  {
    center = DEFAULT_CENTER,
    zoom = DEFAULT_ZOOM,
    tileLayer = 'cartodb_light',
    className = '',
    style = {},
    onMapReady,
    onMapClick,
    onMoveEnd,
    onZoomEnd,
    children,
    scrollWheelZoom = true,
    dragging = true,
    zoomControl = true,
    attributionControl = true,
  },
  ref
) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const tileLayerRef = useRef(null)
  const [isReady, setIsReady] = useState(false)

  // Expose map methods via ref
  useImperativeHandle(ref, () => ({
    getMap: () => mapRef.current,
    panTo: (lat, lng, targetZoom) => {
      if (mapRef.current) {
        mapRef.current.flyTo([lat, lng], targetZoom || mapRef.current.getZoom(), {
          duration: 1,
        })
      }
    },
    fitBounds: (bounds, options = {}) => {
      if (mapRef.current) {
        mapRef.current.fitBounds(bounds, { padding: [50, 50], ...options })
      }
    },
    setView: (lat, lng, targetZoom) => {
      if (mapRef.current) {
        mapRef.current.setView([lat, lng], targetZoom || DEFAULT_ZOOM)
      }
    },
    invalidateSize: () => {
      if (mapRef.current) {
        mapRef.current.invalidateSize()
      }
    },
    getCenter: () => {
      if (mapRef.current) {
        const c = mapRef.current.getCenter()
        return { lat: c.lat, lng: c.lng }
      }
      return null
    },
    getZoom: () => {
      if (mapRef.current) {
        return mapRef.current.getZoom()
      }
      return null
    },
    getBounds: () => {
      if (mapRef.current) {
        return mapRef.current.getBounds()
      }
      return null
    },
  }))

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    // Parse center - support both [lat, lng] and { lat, lng }
    let centerLatLng = DEFAULT_CENTER
    if (Array.isArray(center)) {
      centerLatLng = center
    } else if (center && typeof center === 'object') {
      centerLatLng = [center.lat || DEFAULT_CENTER[0], center.lng || DEFAULT_CENTER[1]]
    }

    // Create map
    const map = L.map(containerRef.current, {
      center: centerLatLng,
      zoom: zoom,
      zoomControl: zoomControl,
      attributionControl: attributionControl,
      scrollWheelZoom: scrollWheelZoom,
      dragging: dragging,
    })

    mapRef.current = map

    // Add tile layer
    const tileConfig = TILE_LAYERS[tileLayer] || TILE_LAYERS.cartodb_light
    tileLayerRef.current = L.tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
      maxZoom: 19,
    }).addTo(map)

    // Event handlers
    if (onMapClick) {
      map.on('click', (e) => {
        onMapClick(e.latlng.lat, e.latlng.lng, e)
      })
    }

    if (onMoveEnd) {
      map.on('moveend', () => {
        const center = map.getCenter()
        onMoveEnd(center.lat, center.lng, map.getZoom())
      })
    }

    if (onZoomEnd) {
      map.on('zoomend', () => {
        onZoomEnd(map.getZoom())
      })
    }

    // Handle resize
    const handleResize = () => {
      if (map) {
        map.invalidateSize()
      }
    }
    window.addEventListener('resize', handleResize)

    // Mark as ready
    setIsReady(true)
    if (onMapReady) {
      onMapReady(map)
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize)
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, []) // Only run once on mount

  // Update center when prop changes
  useEffect(() => {
    if (!mapRef.current || !isReady) return

    let centerLatLng = DEFAULT_CENTER
    if (Array.isArray(center)) {
      centerLatLng = center
    } else if (center && typeof center === 'object') {
      centerLatLng = [center.lat || DEFAULT_CENTER[0], center.lng || DEFAULT_CENTER[1]]
    }

    mapRef.current.setView(centerLatLng, zoom, { animate: false })
  }, [center, zoom, isReady])

  // Update tile layer when prop changes
  useEffect(() => {
    if (!mapRef.current || !isReady) return

    const tileConfig = TILE_LAYERS[tileLayer] || TILE_LAYERS.cartodb_light

    // Remove old layer
    if (tileLayerRef.current) {
      mapRef.current.removeLayer(tileLayerRef.current)
    }

    // Add new layer
    tileLayerRef.current = L.tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
      maxZoom: 19,
    }).addTo(mapRef.current)
  }, [tileLayer, isReady])

  return (
    <div
      ref={containerRef}
      className={`w-full h-full ${className}`}
      style={{ minHeight: '300px', background: '#E8E0D5', ...style }}
    >
      {/* Loading overlay */}
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Đang tải bản đồ...</p>
          </div>
        </div>
      )}
    </div>
  )
})

export default LeafletMap
